<script setup lang="ts">
import { AdobeEventTypeEnum } from "@/models";
import { useCustomerStore, useDataLayerStore, useGlobalStore } from "@/stores";
import { onMounted } from "vue";
import { PageEnum } from "@/enums";
import JourneyLayout from "@/components/layout/JourneyLayout/JourneyLayout.vue";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import InfoCard from "@/components/general/InfoCard/InfoCard.vue";
import welcomePack from "@img/welcomePack.jpg";
import questionsIcon from "@img/questionsIcon.jpg";
import trustIcon from "@img/trustIcon.jpg";
import dateIcon from "@img/dateIcon.jpg";

const global = useGlobalStore();
const customer = useCustomerStore();
const dataLayer = useDataLayerStore();

onMounted(() => {
    dataLayer.triggerAdobeEvent(AdobeEventTypeEnum.FormComplete);
});
</script>

<template>
    <JourneyLayout class="confirmation-page">
        <template #main>
            <div :id="PageEnum.ConfirmationPage">
                <PageHeading>
                    Thank you {{ customer.title }} {{ customer.firstname }}
                    {{ customer.middlename }} {{ customer.surname }}! Your cover
                    starts now.
                </PageHeading>
                <p>
                    We have received your application and we're pleased to
                    confirm that your cover will start immediately. You now have
                    some peace of mind knowing that you're taking steps to help
                    provide a fixed cash sum to help loved ones.
                </p>
                <h3 class="confirmation-subtitle"> What happens next? </h3>
                <div class="card-container">
                    <InfoCard :image-src="welcomePack" alt="Welcome Pack">
                        <p>
                            You will receive an email shortly explaining how to
                            access your policy documents via our online portal,
                            My Account. If you also requested a paper copy, this
                            will be sent within the next seven days.
                        </p>
                        <div v-if="global.currentPromo.length">
                            <p>
                                £{{ global.currentPromo[0].giftAmount }} gift
                                card – Check the terms and conditions to find
                                out when you will receive your gift card.
                            </p>
                        </div>
                    </InfoCard>

                    <InfoCard :image-src="questionsIcon" alt="Contact Us">
                        <p>
                            Please contact us if you need your documents in a
                            different format e.g., Braille, Large Print or
                            Audio. Call us on
                            <a
                                id="contact-number"
                                class="inline-rTap rTapNumber407699"
                                href="tel:0345 026 0042"
                            >
                                0345 026 0042
                            </a>
                            . Charges may apply. We're available Monday to
                            Friday, 9am to 5pm and we may record and monitor
                            calls.
                        </p>
                    </InfoCard>

                    <InfoCard
                        :image-src="trustIcon"
                        alt="Over 50s Life Insurance Plan"
                    >
                        <p>
                            Now that you have an Over 50s Life Insurance Plan
                            you may want to think about placing it in Trust.
                            Putting your plan in trust can help ensure the right
                            people receive the benefit of your plan.
                        </p>
                    </InfoCard>

                    <InfoCard :image-src="dateIcon" alt="Collection date">
                        <p>
                            It may not be possible to collect the first premium
                            on your preferred collection date. Further
                            information will be included in the welcome pack.
                        </p>
                    </InfoCard>
                </div>
                <p
                    >Thanks again for choosing us for your Over 50s Fixed Life
                    Insurance Plan.
                </p>
            </div>
        </template>
        <template #aside />
    </JourneyLayout>
</template>

<style lang="css" scoped>
.confirmation-page {
    background: var(--color-background-alt);
    padding: 0 15px;
    margin: 0 auto;
}

.confirmation-subtitle {
    font-size: 20px;
    font-weight: 400;
}

.card-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;
}

@media (width <= 585px) {
    .card-container {
        grid-template-columns: 1fr;
    }
}
</style>
